import {
    hourMinuteIdFactory,
    hourMinuteIdZodSchema,
} from "@@/shared/date-time/hour-minute-id";
import type { ZodSchemaOf } from "@@/shared/zod-helpers";
import { repeatCount } from "@@/utilities/repeat";
import { z } from "zod";
import { asDate } from "./as-date";
import type { IsoAndUnixTimestamp } from "./iso-and-unix";

const hours: Hour[] = repeatCount({ countFrom: 0, countTo: 23 }) as Hour[];
const getMinutes = (step: Minute = 1): Minute[] =>
    repeatCount({
        countFrom: 0,
        countTo: 59,
        step,
    }) as Minute[];
const minutes: Minute[] = getMinutes();

// type HourMinute = {
//     _id: HourMinuteId;
//     _type: "HOUR_MINUTE";
//     formatted: `${Hour}:${Minute}`;
//     hour: Hour;
//     minute: Minute;
// };

const hourMinuteZodObject = z.object({
    _id: hourMinuteIdZodSchema,
    _type: z.literal("HOUR_MINUTE"),
    formatted: z.string().refine(value => {
        const [hour, minute] = value.split(":").map(Number) as [Hour, Minute];
        return hours.includes(hour) && minutes.includes(minute);
    }) as unknown as ZodSchemaOf<`${Hour}:${Minute}`>,
    hour: z.number().int().min(0).max(23) as unknown as ZodSchemaOf<Hour>,
    minute: z.number().int().min(0).max(59) as unknown as ZodSchemaOf<Minute>,
});

type HourMinute = z.infer<typeof hourMinuteZodObject>;

function hourMinuteFactory(params: { date: Date }): HourMinute;
function hourMinuteFactory(params: { date: IsoAndUnixTimestamp }): HourMinute;
function hourMinuteFactory(params: {
    date: Date | IsoAndUnixTimestamp;
}): HourMinute;
function hourMinuteFactory(params: { hour: Hour; minute: Minute }): HourMinute;
function hourMinuteFactory(
    params:
        | { date: Date | IsoAndUnixTimestamp }
        | { date: Date }
        | { date: IsoAndUnixTimestamp | Date }
        | { hour: Hour; minute: Minute },
): HourMinute {
    if ("date" in params) {
        const _date = asDate(params.date);
        return hourMinuteFactory({
            hour: _date.getHours() as Hour,
            minute: _date.getMinutes() as Minute,
        });
    }
    const { hour, minute } = params;
    const formatted =
        `${hour.toLocaleString().padStart(2, "0")}:${minute.toLocaleString().padStart(2, "0")}` as `${Hour}:${Minute}`;
    return {
        _id: hourMinuteIdFactory(),
        _type: "HOUR_MINUTE",
        formatted,
        hour,
        minute,
    };
}

export { getMinutes, hourMinuteFactory, hourMinuteZodObject, hours, minutes };
export type { Hour, HourMinute, Minute };

type Hour =
    | 0
    | 1
    | 2
    | 3
    | 4
    | 5
    | 6
    | 7
    | 8
    | 9
    | 10
    | 11
    | 12
    | 13
    | 14
    | 15
    | 16
    | 17
    | 18
    | 19
    | 20
    | 21
    | 22
    | 23;
type Minute =
    | 0
    | 1
    | 2
    | 3
    | 4
    | 5
    | 6
    | 7
    | 8
    | 9
    | 10
    | 11
    | 12
    | 13
    | 14
    | 15
    | 16
    | 17
    | 18
    | 19
    | 20
    | 21
    | 22
    | 23
    | 24
    | 25
    | 26
    | 27
    | 28
    | 29
    | 30
    | 31
    | 32
    | 33
    | 34
    | 35
    | 36
    | 37
    | 38
    | 39
    | 40
    | 41
    | 42
    | 43
    | 44
    | 45
    | 46
    | 47
    | 48
    | 49
    | 50
    | 51
    | 52
    | 53
    | 54
    | 55
    | 56
    | 57
    | 58
    | 59;
